import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const SuicideIntentList = ({ assessments, onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Ensure we calculate total_score and risk_level if missing
    const records = (assessments || []).map((r) => ({
        ...r,
        total_score: r.total_score ?? 0,
        risk_level: r.risk_level ?? "—",
        date_assessed: r.date_assessed ?? r.created_at,
    }));

    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) => v != null && String(v).toLowerCase().includes(filterText.toLowerCase()))
    );

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleDeleteConfirm = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This Suicide Intent Scale will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed && onDelete) {
                onDelete(id);
                Swal.fire("Deleted!", "Assessment deleted successfully.", "success");
            }
        });
    };

    const handleExportPDF = () => {
        const doc = new jsPDF("l", "mm", "a4");
        doc.text("Beck’s Suicide Intent Scale Assessments", 14, 15);
        doc.setFontSize(10);

        const tableColumn = ["#", "Patient Name", "Date Assessed", "Total Score", "Risk Level", "Assessed By"];
        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.client_name || "",
            r.date_assessed?.split("T")[0] || "",
            r.total_score,
            r.risk_level,
            r.assessed_by || ""
        ]);

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 25,
            theme: "grid",
            headStyles: { fillColor: [220, 53, 69], textColor: 255 },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: { 4: { halign: "center" } }
        });

        doc.save("suicide_intent_scale_assessments.pdf");
    };

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
                    >
                        {[5, 10, 15, 20].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        { name: "S.No", cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage, width: "80px" },
        { name: "Patient Name", selector: row => row.client_name || "", sortable: true },
        { name: "Date Assessed", selector: row => row.date_assessed?.split("T")[0] || "", sortable: true },
        { name: "Total Score", selector: row => row.total_score, sortable: true, center: true },
        {
            name: "Risk Level",
            cell: row => {
                let bg = "bg-success-subtle text-success";
                if (row.total_score >= 11 && row.total_score <= 19) bg = "bg-warning-subtle text-warning";
                if (row.total_score >= 20) bg = "bg-danger-subtle text-danger";

                return <span className={`badge ${bg} rounded-pill px-4 py-2`}>{row.risk_level}</span>;
            },
            sortable: true,
            center: true,
        },
        { name: "Assessed By", selector: row => row.assessed_by || "", sortable: true },
        {
            name: "Actions",
            cell: row => (
                <div className="d-flex justify-content-center gap-2">
                    {onEdit && <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(row.id)}><i className="ri-edit-line"></i></button>}
                    {onDelete && <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteConfirm(row.id)}><i className="ri-delete-bin-line"></i></button>}
                </div>
            ),
            width: "130px",
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
                            <li>
                                <CSVLink data={filteredRecords} filename="suicide_intent_assessments.csv" className="dropdown-item">
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search assessments..."
                        className="form-control form-control-sm"
                        style={{ width: "300px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                <div>
                    {onAdd && (
                        <button className="btn btn-sm btn-primary" onClick={onAdd}>
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={filteredRecords}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={totalRows}
                onChangeRowsPerPage={setRowsPerPage}
                onChangePage={setCurrentPage}
                paginationComponent={CustomPagination}
                highlightOnHover
                striped
                responsive
                noDataComponent={
                    <div className="p-5 text-center text-muted">
                        <i className="ri-alarm-warning-line fs-1 d-block mb-3 text-danger"></i>
                        No Suicide Intent Scale assessments found
                    </div>
                }
            />
        </div>
    );
};

export default SuicideIntentList;
